import { createContext, useContext, useEffect, useState } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('firmpay_user') || 'null'); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('firmpay_token');
    if (token && !user) {
      api.get('/auth/me').then((r) => {
        setUser(r.data);
        localStorage.setItem('firmpay_user', JSON.stringify(r.data));
      }).catch(() => {}).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data?.mfa_required) {
      // Do not persist anything yet. Return the challenge for the login page to handle.
      return { mfa_required: true, challenge_token: data.challenge_token, expires_in: data.expires_in };
    }
    localStorage.setItem('firmpay_token', data.access_token);
    localStorage.setItem('firmpay_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const verifyMfa = async (challenge_token, code, use_backup = false) => {
    const { data } = await api.post('/auth/login/mfa', { challenge_token, code, use_backup });
    localStorage.setItem('firmpay_token', data.access_token);
    localStorage.setItem('firmpay_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const loginWithPasskey = async (email = '') => {
    // Perform the WebAuthn ceremony via the helper; then persist token.
    const { authenticateWithPasskey } = await import('@/lib/webauthnClient');
    const data = await authenticateWithPasskey({
      apiClient: api,
      basePath: '/auth/webauthn',
      email,
    });
    localStorage.setItem('firmpay_token', data.access_token);
    localStorage.setItem('firmpay_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('firmpay_token');
    localStorage.removeItem('firmpay_user');
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, loginWithPasskey, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function hasRole(user, ...roles) {
  return user && roles.includes(user.role);
}
